import { useQuery } from '@tanstack/react-query';
import { HardHat, Building2 } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type InvItem = {
  partida_codigo: string; partida_nombre: string; capitulo: string;
  techo: number; comprometido: number; devengado: number; pagado: number; disponible: number;
  proyectos_obra: number; monto_obras_autorizado: number;
  activos_adquiridos: number; valor_activos_adquiridos: number;
};

export function InversionPage() {
  const { data } = useQuery({
    queryKey: ['inversion'],
    queryFn: () => api<InvItem[]>('/inversion/consolidado'),
  });
  const items = data ?? [];

  const cap5 = items.filter(i => i.capitulo === '5');
  const cap6 = items.filter(i => i.capitulo === '6');

  const tot5 = cap5.reduce((a, b) => a + b.techo, 0);
  const tot6 = cap6.reduce((a, b) => a + b.techo, 0);
  const ej5 = cap5.reduce((a, b) => a + b.devengado, 0);
  const ej6 = cap6.reduce((a, b) => a + b.devengado, 0);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Presupuesto · Inversión pública</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Inversión pública consolidada</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Gasto capitalizable (capítulos 5000 — Bienes muebles/inmuebles/intangibles y 6000 — Inversión pública) cruzado con obras y activos fijos.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <KPI label="Cap. 5000 autorizado" value={fmt.money(tot5)} sub={`${cap5.reduce((a, b) => a + b.activos_adquiridos, 0)} activos`} icon={<Building2 size={16} />} />
        <KPI label="Cap. 5000 devengado" value={fmt.money(ej5)} />
        <KPI label="Cap. 6000 autorizado" value={fmt.money(tot6)} sub={`${cap6.reduce((a, b) => a + b.proyectos_obra, 0)} proyectos`} icon={<HardHat size={16} />} />
        <KPI label="Cap. 6000 devengado" value={fmt.money(ej6)} />
      </section>

      {[
        { title: 'Capítulo 5000 — Bienes Muebles, Inmuebles e Intangibles', items: cap5, icon: <Building2 size={14} className="text-brand-600" /> },
        { title: 'Capítulo 6000 — Inversión Pública', items: cap6, icon: <HardHat size={14} className="text-brand-600" /> },
      ].map(sec => (
        <Card key={sec.title}>
          <CardHeader title={sec.title} action={sec.icon} />
          <CardBody className="p-0">
            {sec.items.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-ink-mute">Sin presupuesto asignado.</div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Partida</th>
                    <th className="px-5 py-3 text-right">Techo</th>
                    <th className="px-5 py-3 text-right">Comprometido</th>
                    <th className="px-5 py-3 text-right">Devengado</th>
                    <th className="px-5 py-3 text-right">Pagado</th>
                    <th className="px-5 py-3 text-right">Disponible</th>
                    <th className="px-5 py-3 text-center">Obras</th>
                    <th className="px-5 py-3 text-center">Activos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sec.items.map((it, i) => {
                    const ejerPct = it.techo > 0 ? (it.devengado * 100 / it.techo) : 0;
                    return (
                      <tr key={i}>
                        <td className="px-5 py-3">
                          <div className="num">{it.partida_codigo}</div>
                          <div className="text-[11px] text-ink-mute">{it.partida_nombre}</div>
                        </td>
                        <td className="num px-5 py-3 text-right font-semibold">{fmt.money(it.techo)}</td>
                        <td className="num px-5 py-3 text-right">{fmt.money(it.comprometido)}</td>
                        <td className="num px-5 py-3 text-right">
                          {fmt.money(it.devengado)}
                          <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
                            <div className={cn('h-full',
                              ejerPct > 100 ? 'bg-rose-500' : ejerPct > 80 ? 'bg-amber-500' : 'bg-emerald-500')}
                              style={{ width: `${Math.min(100, ejerPct)}%` }} />
                          </div>
                        </td>
                        <td className="num px-5 py-3 text-right">{fmt.money(it.pagado)}</td>
                        <td className={cn('num px-5 py-3 text-right font-semibold',
                          it.disponible < 0 ? 'text-rose-700' : '')}>
                          {fmt.money(it.disponible)}
                        </td>
                        <td className="num px-5 py-3 text-center">
                          {it.proyectos_obra > 0 ? (
                            <div>
                              <div className="font-semibold">{it.proyectos_obra}</div>
                              <div className="text-[10px] text-ink-mute">{fmt.money(it.monto_obras_autorizado)}</div>
                            </div>
                          ) : '—'}
                        </td>
                        <td className="num px-5 py-3 text-center">
                          {it.activos_adquiridos > 0 ? (
                            <div>
                              <div className="font-semibold">{it.activos_adquiridos}</div>
                              <div className="text-[10px] text-ink-mute">{fmt.money(it.valor_activos_adquiridos)}</div>
                            </div>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function KPI({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
        {icon && <span className="text-ink-soft">{icon}</span>}
      </div>
      <div className="num mt-3 text-[20px] font-semibold text-ink">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-ink-mute">{sub}</div>}
    </div>
  );
}
